// Google Analytics 4 - Event Tracking Helper
// PRIMA LEGGO - Analytics Events

/**
 * Track generic event (Google Analytics)
 * @param {string} eventName - Nome evento
 * @param {object} params - Parametri aggiuntivi
 */
export const trackEvent = (eventName, params = {}) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, params);
    console.log('📊 Analytics Event:', eventName, params);
  }
};

/**
 * Track Facebook Pixel event
 * @param {string} eventName - Nome evento Facebook
 * @param {object} params - Parametri evento
 */
export const trackFacebookEvent = (eventName, params = {}) => {
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq('track', eventName, params);
    console.log('📘 Facebook Pixel:', eventName, params);
  }
};

/**
 * Track page view (automatic with GA4 but can be used for custom views)
 * @param {string} pageTitle - Titolo pagina
 * @param {string} pagePath - Path pagina
 */
export const trackPageView = (pageTitle, pagePath) => {
  trackEvent('page_view', {
    page_title: pageTitle,
    page_path: pagePath,
  });
};

/**
 * Track quando user clicca "Carica contratto"
 */
export const trackUploadStart = () => {
  trackEvent('upload_contract_start', {
    event_category: 'engagement',
    event_label: 'Upload Zone Opened',
  });
  // Facebook Pixel: Lead event
  trackFacebookEvent('Lead', {
    content_name: 'Upload Contract',
    content_category: 'Contract Analysis',
  });
};

/**
 * Track quando file viene caricato con successo
 * @param {string} fileType - Tipo file (pdf, jpg, png)
 * @param {number} fileSize - Size in bytes
 */
export const trackUploadComplete = (fileType, fileSize) => {
  trackEvent('upload_contract_complete', {
    event_category: 'conversion',
    event_label: 'File Uploaded',
    file_type: fileType,
    file_size: fileSize,
  });
  // Facebook Pixel: CompleteRegistration (considerato come "onboarding")
  trackFacebookEvent('CompleteRegistration', {
    content_name: 'Contract Uploaded',
    status: 'success',
  });
};

/**
 * Track quando si apre il modal di pagamento
 * @param {string} contractId - ID contratto
 */
export const trackPaymentModalOpen = (contractId) => {
  trackEvent('payment_modal_open', {
    event_category: 'engagement',
    event_label: 'Payment Modal Opened',
    contract_id: contractId,
  });
};

/**
 * Track selezione tipo report
 * @param {string} reportType - 'basic' o 'premium'
 * @param {number} price - Prezzo selezionato
 */
export const trackReportTypeSelected = (reportType, price) => {
  trackEvent('payment_type_selected', {
    event_category: 'engagement',
    event_label: `Report ${reportType}`,
    report_type: reportType,
    value: price,
    currency: 'EUR',
  });
};

/**
 * Track redirect a Stripe
 * @param {string} reportType - Tipo report
 * @param {string} email - Email user
 */
export const trackRedirectToStripe = (reportType, email) => {
  trackEvent('redirect_to_stripe', {
    event_category: 'conversion',
    event_label: 'Redirect to Payment',
    report_type: reportType,
    user_email: email,
  });
  // Facebook Pixel: InitiateCheckout
  trackFacebookEvent('InitiateCheckout', {
    content_name: `Report ${reportType}`,
    content_category: 'Contract Analysis',
    value: reportType === 'premium' ? 5.00 : 2.00,
    currency: 'EUR',
  });
};

/**
 * Track pagamento completato
 * @param {string} contractId - ID contratto
 * @param {string} reportType - Tipo report
 * @param {number} price - Prezzo pagato
 */
export const trackPaymentSuccess = (contractId, reportType, price) => {
  trackEvent('purchase', {
    event_category: 'conversion',
    event_label: 'Payment Completed',
    transaction_id: contractId,
    value: price,
    currency: 'EUR',
    items: [
      {
        item_id: reportType,
        item_name: `Report ${reportType}`,
        item_category: 'Analisi Contratto',
        price: price,
        quantity: 1,
      },
    ],
  });
  // Facebook Pixel: Purchase
  trackFacebookEvent('Purchase', {
    content_name: `Report ${reportType}`,
    content_type: 'product',
    content_ids: [contractId],
    value: price,
    currency: 'EUR',
  });
};

/**
 * Track visualizzazione report
 * @param {string} contractId - ID contratto
 * @param {string} reportType - Tipo report
 */
export const trackReportView = (contractId, reportType) => {
  trackEvent('report_view', {
    event_category: 'engagement',
    event_label: 'Report Viewed',
    contract_id: contractId,
    report_type: reportType,
  });
};

/**
 * Track click su CTA principali
 * @param {string} ctaName - Nome CTA
 * @param {string} location - Dove si trova il CTA
 */
export const trackCTAClick = (ctaName, location) => {
  trackEvent('cta_click', {
    event_category: 'engagement',
    event_label: ctaName,
    cta_location: location,
  });
};

/**
 * Track scroll a sezione specifica
 * @param {string} sectionName - Nome sezione
 */
export const trackSectionView = (sectionName) => {
  trackEvent('section_view', {
    event_category: 'engagement',
    event_label: `Section: ${sectionName}`,
  });
};

/**
 * Track espansione FAQ
 * @param {string} question - Domanda espansa
 */
export const trackFAQExpand = (question) => {
  trackEvent('faq_expand', {
    event_category: 'engagement',
    event_label: question,
  });
};

/**
 * Track click su esempio report
 * @param {string} reportType - 'basic' o 'premium'
 */
export const trackExampleReportView = (reportType) => {
  trackEvent('example_report_view', {
    event_category: 'engagement',
    event_label: `Example ${reportType}`,
    report_type: reportType,
  });
};

/**
 * Track errori
 * @param {string} errorType - Tipo errore
 * @param {string} errorMessage - Messaggio errore
 */
export const trackError = (errorType, errorMessage) => {
  trackEvent('error', {
    event_category: 'error',
    event_label: errorType,
    error_message: errorMessage,
  });
};

export default {
  trackEvent,
  trackFacebookEvent,
  trackPageView,
  trackUploadStart,
  trackUploadComplete,
  trackPaymentModalOpen,
  trackReportTypeSelected,
  trackRedirectToStripe,
  trackPaymentSuccess,
  trackReportView,
  trackCTAClick,
  trackSectionView,
  trackFAQExpand,
  trackExampleReportView,
  trackError,
};
